// Shared low-poly safari parts — `tube` lays a low-poly taper between two
// arbitrary points (the workhorse for trunks, necks, tails, bent limbs and
// lyre horns — no hand-derived Euler maths at the call site); `bands` wraps a
// run of thin ellipsoid slices over a torso barrel (the striped construction
// behind a zebra or tiger, each slice hugging the body's curve); `eyes` is the
// mirrored eye-dot pair nearly every head wants. Ported from age-of-mcp's
// v2/safari-parts.ts: the pure geometry is kept verbatim, with the Part type
// taken from the library.

import * as THREE from "three";
import type { Part } from "../types";

export type Vec3 = readonly [number, number, number];

/** A mirrored pair of round eye dots at z = ±z. */
export function eyes(
  x: number, y: number, z: number,
  r = 0.0035, color: THREE.ColorRepresentation = "#16130e",
): Part[] {
  return [z, -z].map((zz): Part => ({
    geometry: new THREE.IcosahedronGeometry(r, 0), color, position: [x, y, zz],
  }));
}

/**
 * A low-poly taper laid from `from` to `to` (radius `rFrom` at the start,
 * `rTo` at the end). Orientation solved with the XYZ-euler identity
 * R(rx, 0, rz) · ŷ = (−sin rz, cos rz·cos rx, cos rz·sin rx), so any
 * direction is reachable with two angles.
 */
export function tube(
  from: Vec3, to: Vec3, rFrom: number, rTo: number,
  color: THREE.ColorRepresentation, radial = 4,
): Part {
  const dx = to[0] - from[0], dy = to[1] - from[1], dz = to[2] - from[2];
  const len = Math.hypot(dx, dy, dz) || 1e-5;
  const rz = Math.asin(Math.max(-1, Math.min(1, -dx / len)));
  const rx = Math.atan2(dz / len, dy / len);
  return {
    geometry: new THREE.CylinderGeometry(rTo, rFrom, len, radial),
    color,
    position: [from[0] + dx / 2, from[1] + dy / 2, from[2] + dz / 2],
    rotation: [rx, 0, rz],
  };
}

export interface BandOpts {
  /** Band centres along the torso axis (x). */
  xs: readonly number[];
  /** Torso centre height the bands wrap at. */
  y: number;
  /** Band radius — a touch larger than the torso so each slice shows. */
  r: number;
  /** Per-axis scale of each slice: thickness along x, then y/z hug factors. */
  scale: readonly [number, number, number];
  color: THREE.ColorRepresentation;
  /** Lean of every band (z-rotation) — e.g. angled haunch stripes. */
  tilt?: number;
}

/** A run of thin vertical ellipsoid slices wrapped over a torso barrel —
 *  the banded-parts construction behind the zebra's and tiger's stripes. */
export function bands(o: BandOpts): Part[] {
  return o.xs.map((x): Part => ({
    geometry: new THREE.IcosahedronGeometry(o.r, 0),
    color: o.color,
    position: [x, o.y, 0],
    rotation: [0, 0, o.tilt ?? 0],
    scale: [o.scale[0], o.scale[1], o.scale[2]],
  }));
}
